import React, { useState, useEffect, useCallback } from 'react';
import { Eye, EyeOff, Trash2, Inbox, Filter } from 'lucide-react';
import { toast } from 'react-toastify';
import {
    getSuggestions,
    getUnseenSuggestions,
    setSuggestionSeen,
    setSuggestionUnseen,
    deleteSuggestion,
} from '../../services/controlPanelApi';
import Spinner from '../Spinner/Spinner';
import './SuggestionTable.css';

const FILTER_OPTIONS = ['Todas', 'Category', 'Location', 'Report', 'Other'];

const normalizeType = (type) => {
    const known = ['Category', 'Location', 'Report'];
    return known.includes(type) ? type : 'Other';
};

const SuggestionsTable = () => {
    const [suggestions, setSuggestions] = useState([]);
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('Todas');

    const applyFilter = useCallback((data, currentFilter) => {
        if (currentFilter === 'Todas') return data;
        return data.filter((s) => normalizeType(s.type) === currentFilter);
    }, []);

    const fetchSuggestions = useCallback(async () => {
        setLoading(true);
        try {
            const data = showAll ? await getSuggestions() : await getUnseenSuggestions();
            setSuggestions(data || []);
            setFilteredSuggestions(applyFilter(data || [], filter));
        } catch (err) {
            console.error('Error cargando sugerencias:', err);
            toast.error('No se pudieron cargar las sugerencias.');
        } finally {
            setLoading(false);
        }
    }, [showAll, filter, applyFilter]);

    useEffect(() => {
        fetchSuggestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showAll]);

    useEffect(() => {
        setFilteredSuggestions(applyFilter(suggestions, filter));
    }, [filter, suggestions, applyFilter]);

    const handleToggleSeen = async (id, seen) => {
        setLoading(true);
        try {
            if (seen) await setSuggestionUnseen(id);
            else await setSuggestionSeen(id);
            await fetchSuggestions();
        } catch (err) {
            console.error('Error toggling seen:', err);
            toast.error('No se pudo actualizar el estado de la sugerencia.');
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar esta sugerencia?')) return;
        setLoading(true);
        try {
            await deleteSuggestion(id);
            toast.success('Sugerencia eliminada');
            await fetchSuggestions();
        } catch (err) {
            console.error('Error deleting:', err);
            toast.error('No se pudo eliminar la sugerencia.');
            setLoading(false);
        }
    };

    const unseenCount = suggestions.filter((s) => !s.seen).length;

    return (
        <div className="cme-sugg">
            {loading && <Spinner />}

            <header className="cme-sugg__header">
                <div>
                    <h1 className="cme-sugg__title">Sugerencias de usuarios</h1>
                    <p className="cme-sugg__subtitle">
                        {showAll
                            ? `${filteredSuggestions.length} sugerencias en total`
                            : `${unseenCount} sin leer`}
                    </p>
                </div>

                <div className="cme-sugg__toggle-group" role="group" aria-label="Vista">
                    <button
                        type="button"
                        className={`cme-sugg__toggle-btn ${!showAll ? 'is-active' : ''}`}
                        onClick={() => setShowAll(false)}
                        aria-pressed={!showAll}
                    >
                        Sin leer
                    </button>
                    <button
                        type="button"
                        className={`cme-sugg__toggle-btn ${showAll ? 'is-active' : ''}`}
                        onClick={() => setShowAll(true)}
                        aria-pressed={showAll}
                    >
                        Todas
                    </button>
                </div>
            </header>

            <div className="cme-sugg__filters" role="tablist" aria-label="Filtrar por tipo">
                <span className="cme-sugg__filters-label">
                    <Filter size={14} />
                    <span>Tipo:</span>
                </span>
                {FILTER_OPTIONS.map((opt) => (
                    <button
                        key={opt}
                        type="button"
                        className={`cme-sugg__chip ${filter === opt ? 'is-active' : ''}`}
                        onClick={() => setFilter(opt)}
                        aria-pressed={filter === opt}
                    >
                        {opt}
                    </button>
                ))}
            </div>

            {filteredSuggestions.length > 0 ? (
                <div className="cme-sugg__table-wrap">
                    <table className="cme-sugg__table">
                        <thead>
                            <tr>
                                <th>Usuario</th>
                                <th>Tipo</th>
                                <th>Sugerencia</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSuggestions.map((sug) => {
                                const type = normalizeType(sug.type);
                                return (
                                    <tr key={sug.id}>
                                        <td className="cme-sugg__user-cell">{sug.userId || '—'}</td>
                                        <td>
                                            <span className={`cme-sugg__type-badge cme-sugg__type-badge--${type.toLowerCase()}`}>
                                                {type}
                                            </span>
                                        </td>
                                        <td className="cme-sugg__text-cell">{sug.suggestion}</td>
                                        <td>
                                            <span className={`cme-sugg__status ${sug.seen ? 'is-seen' : 'is-unseen'}`}>
                                                {sug.seen ? 'Vista' : 'Sin leer'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="cme-sugg__actions">
                                                <button
                                                    type="button"
                                                    className="cme-sugg__action-btn cme-sugg__action-btn--ghost"
                                                    onClick={() => handleToggleSeen(sug.id, sug.seen)}
                                                    title={sug.seen ? 'Marcar como no vista' : 'Marcar como vista'}
                                                >
                                                    {sug.seen ? <EyeOff size={14} /> : <Eye size={14} />}
                                                    <span>{sug.seen ? 'Marcar no vista' : 'Marcar vista'}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="cme-sugg__action-btn cme-sugg__action-btn--danger"
                                                    onClick={() => handleDelete(sug.id)}
                                                    title="Eliminar sugerencia"
                                                >
                                                    <Trash2 size={14} />
                                                    <span>Eliminar</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                !loading && (
                    <div className="cme-sugg__empty">
                        <div className="cme-sugg__empty-icon">
                            <Inbox size={42} />
                        </div>
                        <p className="cme-sugg__empty-text">No hay sugerencias para mostrar.</p>
                        <p className="cme-sugg__empty-hint">
                            {showAll ? 'Todavía no hay registros.' : 'Todas las sugerencias están marcadas como vistas.'}
                        </p>
                    </div>
                )
            )}
        </div>
    );
};

export default SuggestionsTable;
